import {useState} from 'react'
import PickupTime from '@/customComponents/sharedComponents/PickupTime.jsx'
import {useNavigate} from 'react-router'
import {Popover, PopoverButton, PopoverPanel} from '@headlessui/react'
import {
    locationIcon, identityVerified,
    personCarryingBag, childCarIcon, luggageIcon, arrowRightSm,
    distanceIcon, destinationTimeIcon, cardIcon, greenTick,
    happyRide, unhappyRide, nightmareRide, downloadDoc, downloadIcon,
    driverPlaceHolderCompletedTrip, plusIconLight
} from '@img/index.js'
import TripRouteMap from '@/components/map/TripRouteMap.jsx'
import {
    locationLabel, formatTime, formatPrice, formatShortDate, formatDateTime,
    formatTravelTime, sumQuantity, parseCard
} from '@/utils/functions'
import {formatDHM, addMinutesToAppZone} from '@/utils/dateUtils'
import CardBrand from '@/customComponents/uiComponents/global/CardBrand.jsx'
import {useTripFeedback} from '@/hooks/useFeedback'
import {useTripIssues} from '@/hooks/useIssues'
import ReviewRidePopup from '@/customComponents/RideView/ReviewRidePopup.jsx'
import ReportIssuePopup from '@/customComponents/RideView/ReportIssuePopup.jsx'

const formatCents = (cents) => formatPrice(cents, {decimals: 2})

// Issue lifecycle: Pending → UnderReview → Resolved / Dismissed (admin-driven).
const ISSUE_STATUS_STYLES = {
    Pending: 'bg-white text-(--text-primary)',
    UnderReview: 'bg-white text-[#6086c1]',
    Resolved: 'bg-(--accent-green-light) text-(--text-primary)',
    Dismissed: 'bg-white text-(--text-red)',
}
// "UnderReview" → "Under Review" for display.
const issueStatusLabel = (status) => String(status || '').replace(/([a-z])([A-Z])/g, '$1 $2')

export default function CompletedRideDetails({trip, rideId}) {
    const driver = trip.driver
    const vehicle = trip.selected_vehicle
    const paymentMethod = trip.selected_payment_method
    const {brand, last4} = parseCard(paymentMethod)
    const pricing = vehicle?.pricing
    const pricingBreakdown = vehicle?.pricing_breakdown
    const totalPrice = vehicle?.total_price
    const promoSaved = pricing?.discount_code_discount || 0
    const durationMin = trip.route?.duration_minutes
    const distanceKm = trip.route?.distance_km
    const completedAt = trip.completed_at || trip.ended_at || (trip.scheduled_at && durationMin ? addMinutesToAppZone(trip.scheduled_at, durationMin) : null)
    const passengerCount = trip.passenger_count || sumQuantity(trip.selected_passengers)
    const childSeatCount = sumQuantity(trip.selected_child_seats)
    const luggageCount = sumQuantity(trip.selected_luggage)

    const dropoffTime = completedAt ? formatTime(completedAt) : '--'
    // An initial trip that already spawned a real (non-Draft) return booking can't get another.
    const hasReturnBooking = !!trip.return_booking && trip.return_booking.status !== 'Draft'

    const {data: feedbackData, isLoading: feedbackLoading} = useTripFeedback(rideId)
    // feedback = {by_driver, by_passenger}; an unset side has null rating/feedback.
    const byPassenger = feedbackData?.feedback?.by_passenger
    const passengerReview = byPassenger && (byPassenger.rating != null || byPassenger.feedback) ? byPassenger : null
    const reviewFaceIcon = passengerReview?.rating != null
        ? (passengerReview.rating >= 4 ? happyRide : passengerReview.rating === 3 ? unhappyRide : nightmareRide)
        : null
    const [reviewType, setReviewType] = useState(null)
    const [reportOpen, setReportOpen] = useState(false)
    const navigate = useNavigate()

    const {data: issuesData} = useTripIssues(rideId)
    const reportedIssues = issuesData?.issues || []

    return (
        <div className="grid grid-cols-12 gap-5 max-md:gap-3">
            {/* Left Column */}
            <div className="col-span-12 lg:col-span-9 flex flex-col gap-5 max-md:gap-3">
                {/* Driver card */}
                <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                    <div className="relative overflow-hidden rounded-3xl mb-5 max-md:mb-3 max-md:rounded-2xl">
                        <img src={driverPlaceHolderCompletedTrip} alt="" className="block w-full h-auto"/>
                        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm"/>
                    </div>

                    <div className="relative rounded-3xl overflow-hidden mb-5 max-md:mb-3 max-md:rounded-2xl">
                        <TripRouteMap
                            polyline={trip.route?.polyline}
                            origin={typeof trip.origin === 'object' ? trip.origin : null}
                            destination={typeof trip.destination === 'object' ? trip.destination : null}
                            stopOvers={trip.stop_overs}
                            legs={trip.route?.legs}
                            summary={trip.route?.summary}
                            mapUrl={trip.route?.map_url}
                            className="h-[280px] max-md:h-[200px]"
                        />
                        {completedAt && (
                            <div className="absolute top-3 right-3 bg-(--bg-dark) text-white rounded-xl px-3 py-1.5 text-right">
                                <span className="block font-bold text-sm">{dropoffTime}</span>
                                <span className="block text-xs">{formatShortDate(completedAt)}</span>
                            </div>
                        )}
                    </div>

                    <div className="bg-(--bg-grey) p-5 mb-5 rounded-3xl max-md:mb-0 max-md:p-3">
                        {trip.reference && <p className="text-(--text-secondary) mb-3! max-md:mb-2! max-md:text-xs">Trip {trip.reference}</p>}
                        <div className="relative mb-3 overflow-hidden max-md:text-sm">
                            <span className="absolute h-full w-px left-3 top-5 border-l-2 border-dashed border-gray-900"></span>

                            {/* Origin */}
                            <div className="flex relative mb-6 min-h-7 max-md:mb-4 max-md:flex-col before:absolute before:content-[''] before:w-2 before:h-2 before:bg-gray-900 before:rounded-full before:outline-2 before:outline-gray-900 before:outline-offset-5 before:top-2 before:left-2 max-md:before:h-1.5 max-md:before:w-1.5 max-md:before:outline-offset-3 max-md:before:left-[9px] max-md:before:top-[9px]">
                                <div className="w-1/2 pl-10 max-md:w-full max-md:pl-7"><PickupTime trip={trip} format={formatTime}/></div>
                                <div className="w-1/2 max-md:mt-0.5 max-md:w-full max-md:pl-7">
                                    <span>{locationLabel(trip.origin)}</span>
                                </div>
                            </div>

                            {/* Destination */}
                            <div className="flex relative mb-0 min-h-7 max-md:flex-col before:absolute before:content-[''] before:bg-(--bg-grey) before:w-5 before:h-full before:z-10">
                                <img src={locationIcon} alt="destination" className="absolute w-6 h-6 top-0 left-0 z-20 max-md:left-1 max-md:top-0.5 max-md:h-4 max-md:w-4"/>
                                <div className="w-1/2 pl-10 max-md:w-full max-md:pl-7">
                                    <span className="block">{dropoffTime}</span>
                                    {durationMin > 0 && <span className="block text-gray-400 text-xs">{formatTravelTime(durationMin)}</span>}
                                </div>
                                <div className="w-1/2 max-md:mt-0.5 max-md:w-full max-md:pl-7">
                                    <span className="block">{locationLabel(trip.destination)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-full px-4 py-2 flex items-center justify-center gap-2 max-md:px-3 max-md:py-1.5 max-md:text-sm">
                                <img src={distanceIcon} alt=""/>
                                <span className="font-medium">{distanceKm ? `${Math.round(distanceKm)} km` : '--'}</span>
                            </div>
                            <div className="bg-white rounded-full px-4 py-2 flex items-center justify-center gap-2 max-md:px-3 max-md:py-1.5 max-md:text-sm">
                                <img src={destinationTimeIcon} alt=""/>
                                <span className="font-medium">{durationMin ? formatDHM(durationMin) : '--'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feedback card */}
                {!feedbackLoading && !passengerReview && (
                    <div className="border border-(--bg-grey) rounded-3xl p-3 flex items-center justify-between gap-4 flex-wrap max-md:gap-3 max-md:rounded-2xl">
                        <div>
                            <h6 className="font-bold max-md:text-sm!">Was everything seamless? We'd love your feedback.</h6>
                            <p className="mb-0! max-md:text-xs">Tap the face that matches your ride</p>
                        </div>
                        <div className="flex items-center gap-2 max-w-[40%] max-md:m-auto max-md:max-w-[70%]">
                            <button type="button" onClick={() => setReviewType('happy')} className="cursor-pointer bg-transparent border border-(--bg-grey) p-0 rounded-md overflow-hidden">
                                <img src={happyRide} alt="Yes give me more"/>
                            </button>
                            <button type="button" onClick={() => setReviewType('unhappy')} className="cursor-pointer bg-transparent border border-(--bg-grey) p-0 rounded-md overflow-hidden">
                                <img src={unhappyRide} alt="My boring ride"/>
                            </button>
                            <button type="button" onClick={() => setReviewType('nightmare')} className="cursor-pointer bg-transparent border border-(--bg-grey) p-0 rounded-md overflow-hidden">
                                <img src={nightmareRide} alt="My worst ride"/>
                            </button>
                        </div>
                    </div>
                )}

                {/* Submitted review */}
                {passengerReview && (
                    <div className="border border-(--bg-grey) rounded-3xl p-5 max-md:p-3 max-md:rounded-2xl max-md:text-sm">
                        <div className="flex items-start justify-between gap-4 flex-wrap mb-4 max-md:mb-3">
                            <div>
                                <h6 className="font-bold max-md:text-sm!">Your review</h6>
                                <p className="text-(--text-secondary) mb-0!">Thanks for sharing your feedback with us.</p>
                            </div>
                            {reviewFaceIcon && <img src={reviewFaceIcon} alt="" className="h-14 max-md:h-10"/>}
                        </div>
                        {passengerReview.feedback && (
                            <div className="bg-(--bg-grey) rounded-2xl p-4">
                                {/* whitespace-pre-line: the feedback text is composed with one
                                    answered question per line (see ReviewRidePopup). */}
                                <p className="mb-0! whitespace-pre-line">{passengerReview.feedback}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Reported issues (newest first, from GET /trips/{trip}/issues) */}
                {reportedIssues.length > 0 && (
                    <div className="border border-(--bg-grey) rounded-3xl p-5 max-md:p-3 max-md:rounded-2xl max-md:text-sm">
                        <h6 className="font-bold max-md:text-sm!">Reported issues</h6>
                        <p className="text-(--text-secondary) mb-4!">Our team reviews every report and updates its status here.</p>
                        <div className="flex flex-col gap-3">
                            {reportedIssues.map(issue => (
                                <div key={issue.id} className="bg-(--bg-grey) rounded-2xl p-4">
                                    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                        <span className={`px-4 py-1 rounded-full text-sm font-bold ${ISSUE_STATUS_STYLES[issue.status] || ISSUE_STATUS_STYLES.Pending}`}>{issueStatusLabel(issue.status)}</span>
                                        {issue.created_at && <span className="text-sm text-(--text-secondary)">{formatDateTime(issue.created_at)}</span>}
                                    </div>
                                    {/* whitespace-pre-line: the description is composed with one
                                        selected issue per line (see ReportIssuePopup). */}
                                    <p className="mb-0! whitespace-pre-line">{issue.description}</p>
                                    {(issue.image_urls?.length > 0 || issue.video_url) && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {issue.image_urls?.map((url, i) => (
                                                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                                    <img src={url} alt={`Evidence ${i + 1}`} className="w-16 h-16 rounded-xl object-cover"/>
                                                </a>
                                            ))}
                                            {issue.video_url && (
                                                <video src={issue.video_url} controls className="h-16 rounded-xl"/>
                                            )}
                                        </div>
                                    )}
                                    {issue.resolution_note && (
                                        <div className="bg-white rounded-xl p-3 mt-3">
                                            <span className="block font-medium text-sm mb-1">Resolution note</span>
                                            <p className="mb-0!">{issue.resolution_note}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Column */}
            <div className="col-span-12 lg:col-span-3 flex flex-col gap-5 max-md:gap-3">
                {/* Total */}
                <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                    <Popover className="relative group">
                        <PopoverButton className="cursor-pointer w-full flex items-center justify-between bg-transparent text-left outline-none">
                            <h2 className="font-bold mb-0! max-md:text-xl!">Total {formatCents(totalPrice)}</h2>
                            <svg className="w-10 h-10 max-md:h-7 max-md:w-7 shrink-0 transition-transform duration-200 group-data-[open]:rotate-180" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd"/>
                            </svg>
                        </PopoverButton>
                        <PopoverPanel anchor="bottom start" transition className="w-[var(--button-width)] mt-2 rounded-2xl bg-white border border-(--border-color) shadow-lg p-5 z-[999] transition duration-200 ease-out origin-top data-[closed]:opacity-0 data-[closed]:scale-95">
                            <div className="flex flex-col gap-3">
                                {pricingBreakdown?.length > 0 ? (
                                    <>
                                        {pricingBreakdown.filter(item => item.value && item.value !== 0).map((item, i) => (
                                            <div key={i} className="flex justify-between">
                                                <span className="text-(--text-secondary)">{item.label}</span>
                                                <span className={`font-medium ${item.type === 'discount' ? 'text-(--text-green)' : ''}`}>
                                                    {item.type === 'discount' ? `-${formatCents(Math.abs(item.value))}` : formatCents(item.value)}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between border-t border-(--border-color) pt-3">
                                            <span className="font-bold">Total</span>
                                            <span className="font-bold">{formatCents(totalPrice)}</span>
                                        </div>
                                    </>
                                ) : pricing ? (
                                    <>
                                        {pricing.base_fare > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Base fare</span><span className="font-medium">{formatCents(pricing.base_fare)}</span></div>}
                                        {pricing.distance_fare > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Distance fare</span><span className="font-medium">{formatCents(pricing.distance_fare)}</span></div>}
                                        {pricing.vehicle_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Vehicle charge</span><span className="font-medium">{formatCents(pricing.vehicle_charge)}</span></div>}
                                        {pricing.child_seat_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Child seat</span><span className="font-medium">{formatCents(pricing.child_seat_charge)}</span></div>}
                                        {pricing.luggage_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Luggage</span><span className="font-medium">{formatCents(pricing.luggage_charge)}</span></div>}
                                        {pricing.stopover_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Stopover</span><span className="font-medium">{formatCents(pricing.stopover_charge)}</span></div>}
                                        {pricing.airport_fee > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Airport fee</span><span className="font-medium">{formatCents(pricing.airport_fee)}</span></div>}
                                        {pricing.surge_charge > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Surge</span><span className="font-medium">{formatCents(pricing.surge_charge)}</span></div>}
                                        {pricing.tax > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Tax</span><span className="font-medium">{formatCents(pricing.tax)}</span></div>}
                                        {pricing.gratuity > 0 && (() => {
                                            const gratuityPct = pricing.gratuity_percentage ?? vehicle?.gratuity_percentage
                                            return <div className="flex justify-between"><span className="text-(--text-secondary)">Gratuity{gratuityPct != null ? ` (${gratuityPct}%)` : ''}</span><span className="font-medium">{formatCents(pricing.gratuity)}</span></div>
                                        })()}
                                        {pricing.online_discount > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Online discount</span><span className="font-medium text-(--text-green)">-{formatCents(pricing.online_discount)}</span></div>}
                                        {pricing.discount_code_discount > 0 && <div className="flex justify-between"><span className="text-(--text-secondary)">Promo ({pricing.discount_code})</span><span className="font-medium text-(--text-green)">-{formatCents(pricing.discount_code_discount)}</span></div>}
                                        <div className="flex justify-between border-t border-(--border-color) pt-3">
                                            <span className="font-bold">Total</span>
                                            <span className="font-bold">{formatCents(pricing.total)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-(--text-secondary) mb-0!">No pricing details available</p>
                                )}
                            </div>
                        </PopoverPanel>
                    </Popover>
                    {promoSaved > 0 && (
                        <span className="bg-(--bg-grey) text-center rounded-xs font-bold w-full block mt-3 py-2">You've saved {formatCents(promoSaved)} with a promo</span>
                    )}
                </div>

                {/* Payment Methods (contains Trip info + downloads) */}
                <div className="white-global-section max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                    <div className="flex items-center gap-2 mb-10 max-md:mb-4">
                        <img src={cardIcon} alt="Card" className="max-md:w-6"/>
                        <h4 className="mb-0! max-md:text-base!">Payment Methods</h4>
                    </div>
                    {last4 ? (
                        <div className="bg-(--accent-green-light) text-(--text-primary) rounded-full px-10 py-2.5 inline-flex items-center gap-2 mb-10 max-md:mb-4 max-md:px-5 max-md:py-1.5 max-md:text-sm">
                            <CardBrand brand={brand} className="h-4"/>
                            **** {last4}
                        </div>
                    ) : (
                        <p className="text-(--text-secondary) mb-6">No payment method</p>
                    )}

                    {trip.payment_hold?.is_captured && (
                        <div className="flex items-center gap-2 text-lg mb-10 max-md:mb-4 max-md:text-sm">
                            <img alt="success" src={greenTick} className="mr-2 max-md:w-5 max-md:mr-0"/>
                            <span>Payment captured</span>
                        </div>
                    )}

                    {/* Trip info */}
                    <h5 className="font-bold mb-4 max-md:mb-3! max-md:text-base!">Trip info</h5>
                    <div className="flex items-center justify-between mb-6 max-md:mb-4">
                        <div className="flex">
                            <span className="relative w-20 h-20 bg-(--bg-grey) border-2 border-white rounded-full flex items-center justify-center max-md:h-12 max-md:w-12">
                                <img src={personCarryingBag} alt="" className="max-md:h-6 max-md:w-6 object-contain"/>
                                <span className="absolute -top-1 -right-1 bg-(--text-primary) text-white text-[10px] font-bold w-5 h-5 rounded-full max-md:h-4 max-md:w-4 max-md:text-[9px] flex items-center justify-center">{passengerCount}</span>
                            </span>
                            <span className="relative w-20 h-20 bg-(--bg-grey) border-2 border-white rounded-full flex items-center justify-center max-md:h-12 max-md:w-12 -ml-2">
                                <img src={childCarIcon} alt="" className="max-md:h-6 max-md:w-6 object-contain"/>
                                <span className="absolute -top-1 -right-1 bg-(--text-primary) text-white text-[10px] font-bold w-5 h-5 rounded-full max-md:h-4 max-md:w-4 max-md:text-[9px] flex items-center justify-center">{childSeatCount}</span>
                            </span>
                            <span className="relative w-20 h-20 bg-(--bg-grey) border-2 border-white rounded-full flex items-center justify-center max-md:h-12 max-md:w-12 -ml-2">
                                <img src={luggageIcon} alt="" className="max-md:h-6 max-md:w-6 object-contain"/>
                                <span className="absolute -top-1 -right-1 bg-(--text-primary) text-white text-[10px] font-bold w-5 h-5 rounded-full max-md:h-4 max-md:w-4 max-md:text-[9px] flex items-center justify-center">{luggageCount}</span>
                            </span>
                        </div>
                        {/*<button type="button" className="cursor-pointer w-10 h-10 rounded-full bg-(--bg-grey) flex items-center justify-center border-0">*/}
                        {/*    <img src={arrowRightSm} alt="" className="w-4 h-4"/>*/}
                        {/*</button>*/}
                    </div>

                    {/* Downloads */}
                    {trip.receipt_link && (
                        <a href={trip.receipt_link} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-(--text-primary) underline mb-3 flex items-center gap-2">
                            <img alt="" src={downloadDoc}/>
                            Download this PDF
                        </a>
                    )}
                    {trip.invoice_link && (
                        <a href={trip.invoice_link} target="_blank" rel="noopener noreferrer" className="cursor-pointer w-full py-3 bg-(--bg-dark) text-white font-medium flex items-center justify-center gap-2 border-0 no-underline max-md:py-2 max-md:text-sm">
                            <img alt="" src={downloadIcon}/>
                            Download tax invoice
                        </a>
                    )}

                    <div className="flex justify-center gap-3 flex-wrap w-full mt-5 max-md:mt-3 max-md:gap-2">
                        {!trip.parent_trip && (
                            <span className="relative group">
                                <button type="button" onClick={() => navigate(`/return-booking/${rideId}`)}
                                        disabled={hasReturnBooking}
                                        className="cursor-pointer flex items-center gap-2 py-2 pr-3.5 px-2 bg-(--text-primary) text-white rounded-full transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-80 max-md:text-[12px] max-md:gap-2 max-md:px-3">
                                    <img alt="" src={plusIconLight} className="w-6 max-md:w-5"/>
                                    Return Booking
                                </button>
                                {hasReturnBooking && (
                                    <span className="hidden group-hover:block absolute top-full left-0 mt-2 bg-[#3c393c] text-white rounded-lg px-3 py-2 text-xs text-center w-max max-w-[220px] z-30 after:content-[''] after:absolute after:bottom-full after:left-5 after:border-[6px] after:border-transparent after:border-b-[#3c393c]">This trip already has a return booking: {trip.return_booking.id}</span>
                                )}
                            </span>
                        )}

                        {/* ONE REPORT PER RIDE — disabled once `reportedIssues` has one. */}
                        <button type="button" onClick={() => setReportOpen(true)}
                                disabled={reportedIssues.length > 0}
                                title={reportedIssues.length > 0 ? 'An issue has already been reported for this ride' : undefined}
                                className="cursor-pointer flex items-center gap-2 py-2 pr-3.5 px-2 bg-(--text-primary) text-white rounded-full transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50 max-md:text-[12px] max-md:gap-2 max-md:px-3">
                            <img alt="" src={plusIconLight} className="w-6 max-md:w-5"/>
                            Issue with the ride
                        </button>
                    </div>
                </div>
            </div>

            <ReviewRidePopup isOpen={!!reviewType} onClose={() => setReviewType(null)} tripId={rideId} initialType={reviewType}/>
            <ReportIssuePopup isOpen={reportOpen} onClose={() => setReportOpen(false)} rideId={rideId}/>
        </div>
    )
}
